import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const ROTATION_INTERVAL = 4000;

const ImageGallery = ({ images = [], mainImage, alt = "Image", autoRotate = true }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [paused, setPaused] = useState(false);

  const allImages = [];
  if (mainImage) allImages.push(mainImage);
  if (images?.length) {
    images.forEach((img) => {
      if (img && img !== mainImage) allImages.push(img);
    });
  }

  // Auto-rotate every 4 seconds
  useEffect(() => {
    if (allImages.length <= 1 || !autoRotate || paused) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % allImages.length);
    }, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, [allImages.length, autoRotate, paused]);

  const navigate = (dir) => {
    setSelectedIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return allImages.length - 1;
      if (next >= allImages.length) return 0;
      return next;
    });
  };

  if (allImages.length === 0) return null;
  if (allImages.length === 1) {
    return (
      <img
        src={allImages[0]}
        alt={alt}
        className="w-full h-full object-cover"
        data-testid="single-image"
      />
    );
  }

  return (
    <>
      <div
        className="relative group w-full h-full"
        data-testid="image-gallery"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Carousel images with crossfade */}
        {allImages.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={alt}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 cursor-pointer ${
              i === activeIndex ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setSelectedIndex(activeIndex)}
            data-testid={i === activeIndex ? "gallery-active-image" : undefined}
          />
        ))}

        {/* Dot indicators */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 pointer-events-none">
          {allImages.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === activeIndex ? "w-4 bg-amber-400" : "w-1.5 bg-white/40"
              }`}
            />
          ))}
        </div>

        {/* Image count badge */}
        <span className="absolute top-2 right-2 text-[10px] font-mono bg-black/60 text-white px-2 py-0.5 rounded pointer-events-none z-10">
          {activeIndex + 1}/{allImages.length}
        </span>
      </div>

      {/* Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-4xl bg-[#0B0F19] border-white/10 p-0 overflow-hidden">
          <div className="relative">
            <img
              src={selectedIndex !== null ? allImages[selectedIndex] : ""}
              alt={alt}
              className="w-full max-h-[75vh] object-contain bg-black"
              data-testid="lightbox-image"
            />
            <button
              onClick={() => navigate(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs font-mono text-white/70 bg-black/50 px-3 py-1 rounded-full">
              {selectedIndex !== null ? selectedIndex + 1 : 0} / {allImages.length}
            </div>
          </div>
          <div className="flex gap-2 p-3 overflow-x-auto justify-center bg-[#030712]">
            {allImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setSelectedIndex(i)}
                className={`w-14 h-14 rounded overflow-hidden border-2 flex-shrink-0 transition-all ${
                  selectedIndex === i ? "border-amber-500" : "border-transparent hover:border-white/30"
                }`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;
